// The draft on a TV (M3, DRAFT_ROOM.md "The draft on a TV", B-608): the
// board, the clock, the last pick and the next three, for a draft held in one
// room, on the TV across it while everybody picks on their phones.
//
// IT SHOWS THE ROOM AND DOES NOTHING IN IT: no pick, no chat, no sounds, and
// nothing of one seat's own. Its socket watches (`useLiveDraft`'s `watch`), so
// the room counts it as nobody's presence: a seat whose manager has gone is
// still put on auto, bid for and pushed (the review of 2026-11-01). The whole
// window, with full screen and the shell's Back in a corner that fades while
// nobody touches anything, and the screen kept awake where the browser allows.
//
// THE ROOM'S OWN DATA: its socket while it drafts, the stored board for a
// draft entered offline, drafted by itself or over, asked again every half
// minute, and the simulator's for the practice league.

import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  dailyStopEnds,
  formatInZone,
  DraftView as DraftViewSchema,
  type DraftView,
} from '@fsp/domain';
import {
  deviceZone,
  useDraft,
  useLeagueBase,
  useReaderZone,
  useRouteLeagueId,
} from '../data/hooks';
import { useLeagueSource } from '../data/leagueSource';
import { useLiveDraft, type RoomConnection } from '../data/liveDraft';
import { useRead } from '../data/read';
import { useSim } from '../data/SimProvider';
import { EmptyState, TeamMark, positionLabel } from '../kit';
import { Button, Colored, IconButton } from '../kit/ui';
import { BackButton, ordinal } from '../shell/AppShell';
import { useTier } from '../shell/useTier';
import { useWayBack } from '../shell/wayBack';
import { clubLabel } from '../sports/registry';
import { FromLeague } from './shared/FromLeague';
import { deviceNow, useTicking } from './DraftRoomScreen';
import { isSlow } from './draft/AuctionBlock';
import { clock } from './draft/DraftRoom';
import { pickLabel } from './draft/research';
import '../styles/screens/draft-tv.css';

/** How often the stored board is asked again: a draft running by itself fills it. */
const STORED_EVERY_MS = 30_000;

export function DraftTvScreen() {
  const source = useLeagueSource();
  // EVERY STATE IN THE FRAME, a read that failed included, so none of them is
  // a page with no way back (UX-13).
  return (
    <TvFrame>
      {source === 'api' ? (
        <RealTv />
      ) : source === 'sim' ? (
        <PracticeTv />
      ) : (
        <EmptyState>Opening the draft…</EmptyState>
      )}
    </TvFrame>
  );
}

/** A real league: its room while it drafts there, the stored board otherwise. */
function RealTv() {
  const leagueId = useRouteLeagueId();
  const [asked, setAsked] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setAsked((n) => n + 1), STORED_EVERY_MS);
    return () => clearInterval(t);
  }, []);
  const read = useRead(leagueId === null ? null : `/leagues/${leagueId}/draft`, DraftViewSchema, {
    refresh: asked,
  });
  return (
    <FromLeague
      snapshot={read.snapshot}
      onRetry={read.reload}
      whileLoading={<EmptyState>Opening the draft…</EmptyState>}
    >
      {(view) =>
        view === null ? (
          <EmptyState>This league has no draft to show.</EmptyState>
        ) : view.mode === 'live' && view.status !== 'complete' ? (
          <LiveTv leagueId={view.league.id} />
        ) : (
          <StoredTv view={view} />
        )
      }
    </FromLeague>
  );
}

/** The stored board, its countdown moving while it waits. */
function StoredTv({ view }: { view: DraftView }) {
  const now = useTicking(1_000);
  return <TvBoard view={view} now={now} live={false} connection="live" />;
}

function LiveTv({ leagueId }: { leagueId: string }) {
  // It watches, and sends nothing but its clock's ping, so nothing is refused.
  const live = useLiveDraft(leagueId, () => undefined, deviceNow, null, null, true);
  const now = useTicking() + live.offset;
  if (live.view === null) {
    return (
      <EmptyState>
        {live.connection === 'signed-out'
          ? 'Your session ended. Sign in again to show the draft.'
          : live.connection === 'removed'
            ? 'You are no longer in this league, so the draft cannot be shown here.'
            : live.connection === 'offline'
              ? 'The draft room cannot be reached right now. This page keeps trying.'
              : 'Joining the draft room…'}
      </EmptyState>
    );
  }
  return <TvBoard view={live.view} now={now} live connection={live.connection} />;
}

/** The practice league's room, on the simulator. */
function PracticeTv() {
  const view = useDraft();
  const sim = useSim();
  return <TvBoard view={view} now={sim.now} live connection="live" />;
}

// ── The frame: the whole window, a way back, full screen, kept awake ──────

/** How long the corner's buttons stay with nobody touching anything. */
const IDLE_MS = 4_000;

function TvFrame({ children }: { children: ReactNode }) {
  const base = useLeagueBase();
  const back = useWayBack(false);
  const phone = useTier() === 'phone';
  const [idle, setIdle] = useState(false);
  const [full, setFull] = useState(() => document.fullscreenElement !== null);

  // THE CORNER FADES while nobody touches anything, and comes back with a
  // move, a touch or a key.
  useEffect(() => {
    let timer = setTimeout(() => setIdle(true), IDLE_MS);
    const wake = () => {
      setIdle(false);
      clearTimeout(timer);
      timer = setTimeout(() => setIdle(true), IDLE_MS);
    };
    const events = ['pointermove', 'pointerdown', 'keydown'] as const;
    for (const e of events) window.addEventListener(e, wake);
    return () => {
      clearTimeout(timer);
      for (const e of events) window.removeEventListener(e, wake);
    };
  }, []);

  // THE SCREEN KEPT AWAKE, where the browser allows it, and again when the
  // page is shown after being hidden, which lets the lock go.
  useEffect(() => {
    let lock: WakeLockSentinel | null = null;
    let gone = false;
    const take = async () => {
      try {
        const next = (await navigator.wakeLock?.request('screen')) ?? null;
        if (gone) void next?.release();
        else lock = next;
      } catch {
        // Not allowed here (no secure origin, a battery saver): the screen
        // sleeps as it would anyway.
      }
    };
    void take();
    const shown = () => {
      if (document.visibilityState === 'visible') void take();
    };
    document.addEventListener('visibilitychange', shown);
    return () => {
      gone = true;
      document.removeEventListener('visibilitychange', shown);
      void lock?.release();
    };
  }, []);

  // FULL SCREEN IS THE TV'S, and ends with it: Back from a full screen TV
  // leaves the browser's full screen too, rather than the room in it.
  useEffect(() => {
    const changed = () => setFull(document.fullscreenElement !== null);
    document.addEventListener('fullscreenchange', changed);
    return () => {
      document.removeEventListener('fullscreenchange', changed);
      if (document.fullscreenElement !== null)
        void document.exitFullscreen().catch(() => undefined);
    };
  }, []);
  const toggleFull = () => {
    if (document.fullscreenElement !== null) void document.exitFullscreen();
    else void document.documentElement.requestFullscreen().catch(() => undefined);
  };

  return (
    <div className="fsp-tv" data-idle={idle || undefined}>
      {/* THE WAY BACK, the shell's own (UX-13): to where it was opened from,
          else up to the draft room. */}
      <header className="fsp-tv-corner">
        {/* A phone's screen has room for the icon alone, named. */}
        {!document.fullscreenEnabled ? null : phone ? (
          <IconButton
            icon={full ? 'shrink-screen' : 'full-screen'}
            variant="quiet"
            label={full ? 'Leave full screen' : 'Full screen'}
            onClick={toggleFull}
          />
        ) : (
          <Button
            size="xs"
            variant="quiet"
            icon={full ? 'shrink-screen' : 'full-screen'}
            onClick={toggleFull}
          >
            {full ? 'Leave full screen' : 'Full screen'}
          </Button>
        )}
        {back !== null ? (
          <BackButton label={back.label} onClick={back.go} short={phone} />
        ) : (
          <Button size="xs" variant="quiet" icon="back" to={`${base}/draft`}>
            Draft room
          </Button>
        )}
      </header>
      {children}
    </div>
  );
}

// ── The board ───────────────────────────────────────────────────────────────

function TvBoard({
  view,
  now,
  live,
  connection,
}: {
  view: DraftView;
  /** The room's clock, in milliseconds. */
  now: number;
  /** A draft whose clock runs: the room's or the simulator's. */
  live: boolean;
  connection: RoomConnection;
}) {
  const zone = useReaderZone() ?? deviceZone();
  const seats = useMemo(() => [...view.seats].sort((a, b) => a.slot - b.slot), [view.seats]);
  const seatOf = useMemo(() => new Map(view.seats.map((s) => [s.team.id, s])), [view.seats]);
  const auction = view.auction;
  // A SOCKET THAT HAS ENDED IS NOT A ROOM IN PROGRESS: what it last showed
  // stays, the clock stops, and the head says why.
  const ended = connection === 'signed-out' || connection === 'removed' || connection === 'closed';
  const ticking = live && !ended;
  return (
    <div className="fsp-tv-screen">
      <div className="fsp-tv-head">
        <span className="fsp-tv-league">{view.league.name}</span>
        <span className="fsp-tv-state" data-alert={ended || undefined}>
          {connection === 'signed-out'
            ? 'Your session ended. Sign in again to show the draft.'
            : connection === 'removed'
              ? 'You are no longer in this league.'
              : connection === 'reconnecting' || connection === 'offline'
                ? 'Reconnecting to the room…'
                : view.status === 'lobby'
                  ? 'The draft'
                  : view.status === 'complete'
                    ? 'The draft is over'
                    : auction !== null
                      ? 'The auction'
                      : `Round ${String(view.current?.round ?? 1)} of ${String(view.rounds)}`}
        </span>
      </div>
      <aside className="fsp-tv-side">
        {view.status === 'lobby' ? (
          <Lobby view={view} now={now} zone={zone} seats={seats} />
        ) : view.status === 'complete' ? (
          <Over view={view} seatOf={seatOf} />
        ) : auction !== null ? (
          <OnTheBlock
            view={view}
            now={now}
            live={ticking}
            zone={zone}
            seatOf={seatOf}
            seats={seats}
          />
        ) : (
          <OnTheClock view={view} now={now} live={ticking} zone={zone} seatOf={seatOf} />
        )}
      </aside>
      <Board view={view} seats={seats} auction={auction !== null} />
    </div>
  );
}

type SeatMap = Map<string, DraftView['seats'][number]>;

/** A team as the TV names it: its name, and who runs it. */
function TeamLine({ seat, big = false }: { seat: DraftView['seats'][number]; big?: boolean }) {
  return (
    <span className="fsp-tv-team" data-big={big || undefined}>
      <TeamMark team={seat.team} size={big ? 56 : 28} />
      <span className="fsp-tv-teamtext">
        <span className="fsp-tv-teamname">{seat.team.name}</span>
        <span className="fsp-tv-owner">
          {seat.ai ? `${seat.ai.name} · bot` : seat.team.ownerName}
        </span>
      </span>
    </span>
  );
}

function OnTheClock({
  view,
  now,
  live,
  zone,
  seatOf,
}: {
  view: DraftView;
  now: number;
  live: boolean;
  zone: string;
  seatOf: SeatMap;
}) {
  const current = view.current;
  const seat = current ? seatOf.get(current.teamId) : undefined;
  const n = view.seats.length;
  const deadline = current?.deadlineAt ? Date.parse(current.deadlineAt) : null;
  const remaining = deadline === null ? null : deadline - now;
  const stoppedUntil =
    live && view.overnightPause !== null
      ? dailyStopEnds(view.overnightPause, view.overnightPause.zone, now)
      : null;
  const paused = view.status === 'paused';
  const last = view.lastPick;
  const lastSeat = last ? seatOf.get(last.teamId) : undefined;
  return (
    <>
      <section className="fsp-tv-block">
        <h2 className="fsp-tv-cap">On the clock</h2>
        {seat ? <TeamLine seat={seat} big /> : null}
        {current ? (
          <div className="fsp-tv-pick">
            {`Round ${String(current.round)} · Pick ${String(((current.overall - 1) % n) + 1)} · ${ordinal(current.overall)} overall`}
          </div>
        ) : null}
        <div
          className="fsp-tv-clock fsp-num"
          data-urgent={
            live &&
            !paused &&
            stoppedUntil === null &&
            remaining !== null &&
            remaining > 0 &&
            remaining <= 10_000
              ? 'true'
              : undefined
          }
        >
          {/* A clock only while the room's runs here: not a stored board's,
              nor a socket's that has ended. */}
          {paused
            ? 'Paused'
            : stoppedUntil !== null || !live || remaining === null
              ? '—'
              : clock(remaining)}
        </div>
        {stoppedUntil !== null && !paused ? (
          <div className="fsp-tv-note">
            The clock stops until{' '}
            {formatInZone(stoppedUntil, zone, { hour: 'numeric', minute: '2-digit' })}.
          </div>
        ) : remaining !== null &&
          remaining <= 0 &&
          view.whenTheClockExpires === 'off' &&
          !paused ? (
          <div className="fsp-tv-note">The clock is at zero. This league waits for every pick.</div>
        ) : null}
      </section>
      <section className="fsp-tv-block">
        <h2 className="fsp-tv-cap">Just picked</h2>
        {last?.player ? (
          <Colored
            className="fsp-tv-last"
            colors={{
              '--fsp-pos': `var(--fsp-pos-${(last.player.positions[0] ?? '').toLowerCase()})`,
            }}
          >
            <span className="fsp-tv-lastname">{last.player.name}</span>
            <span className="fsp-tv-lastsub">
              {positionLabel(last.player.positions[0] ?? '')} ·{' '}
              {last.player.proTeam === null ? 'FA' : clubLabel(last.player.proTeam)} ·{' '}
              {pickLabel(last.round, last.slot)}
            </span>
            {lastSeat ? <span className="fsp-tv-lastby">to {lastSeat.team.name}</span> : null}
          </Colored>
        ) : (
          <div className="fsp-tv-note">The first pick is coming.</div>
        )}
      </section>
      {view.upcoming.length > 0 ? (
        <section className="fsp-tv-block">
          <h2 className="fsp-tv-cap">Up next</h2>
          <ol className="fsp-tv-next">
            {view.upcoming.slice(0, 3).map((u) => {
              const s = seatOf.get(u.teamId);
              return s ? (
                <li key={u.overall}>
                  <span className="fsp-tv-nextno fsp-num">{u.overall}</span>
                  <TeamLine seat={s} />
                </li>
              ) : null;
            })}
          </ol>
        </section>
      ) : null}
    </>
  );
}

function OnTheBlock({
  view,
  now,
  live,
  zone,
  seatOf,
  seats,
}: {
  view: DraftView;
  now: number;
  live: boolean;
  zone: string;
  seatOf: SeatMap;
  seats: DraftView['seats'];
}) {
  const a = view.auction!;
  const paused = view.status === 'paused';
  const lot = a.lots[0] ?? null;
  const leader = lot ? seatOf.get(lot.leaderTeamId) : undefined;
  const nominator = a.nominating ? seatOf.get(a.nominating.teamId) : undefined;
  const deadline = lot?.deadlineAt ?? a.nominating?.deadlineAt ?? null;
  const remaining = deadline === null ? null : Date.parse(deadline) - now;
  // THE OVERNIGHT STOP, as the room has it: the clock says nothing through the
  // night, and a slow auction's last seconds are not urgent (the review of
  // 2026-11-01).
  const stoppedUntil =
    live && view.overnightPause !== null
      ? dailyStopEnds(view.overnightPause, view.overnightPause.zone, now)
      : null;
  const money = new Map(a.budgets.map((b) => [b.teamId, b]));
  return (
    <>
      <section className="fsp-tv-block">
        <h2 className="fsp-tv-cap">{lot ? 'On the block' : 'Nominating'}</h2>
        {lot ? (
          <Colored
            className="fsp-tv-last"
            colors={{
              '--fsp-pos': `var(--fsp-pos-${(lot.player.positions[0] ?? '').toLowerCase()})`,
            }}
          >
            <span className="fsp-tv-lastname">{lot.player.name}</span>
            <span className="fsp-tv-lastsub">
              {positionLabel(lot.player.positions[0] ?? '')} ·{' '}
              {lot.player.proTeam === null ? 'FA' : clubLabel(lot.player.proTeam)}
            </span>
            <span className="fsp-tv-bid fsp-num">${lot.price}</span>
            {leader ? <span className="fsp-tv-lastby">{leader.team.name}</span> : null}
          </Colored>
        ) : nominator ? (
          <TeamLine seat={nominator} big />
        ) : null}
        {a.lots.length > 1 ? (
          <div className="fsp-tv-note">{`${String(a.lots.length - 1)} more up for bids`}</div>
        ) : null}
        <div
          className="fsp-tv-clock fsp-num"
          data-urgent={
            live &&
            !paused &&
            stoppedUntil === null &&
            !isSlow(a) &&
            remaining !== null &&
            remaining > 0 &&
            remaining <= 10_000
              ? 'true'
              : undefined
          }
        >
          {paused
            ? 'Paused'
            : stoppedUntil !== null || !live || remaining === null
              ? '—'
              : clock(remaining)}
        </div>
        {stoppedUntil !== null && !paused ? (
          <div className="fsp-tv-note">
            The clock stops until{' '}
            {formatInZone(stoppedUntil, zone, { hour: 'numeric', minute: '2-digit' })}.
          </div>
        ) : null}
      </section>
      <section className="fsp-tv-block">
        <h2 className="fsp-tv-cap">Money left</h2>
        <ul className="fsp-tv-money">
          {seats.map((s) => {
            const b = money.get(s.team.id);
            return b ? (
              <li key={s.team.id}>
                <TeamMark team={s.team} size={20} />
                <span className="fsp-tv-moneyname">{s.team.name}</span>
                <span className="fsp-num">${b.left}</span>
                <span className="fsp-tv-owner">
                  {b.spotsLeft === 1 ? '1 spot' : `${String(b.spotsLeft)} spots`}
                </span>
              </li>
            ) : null;
          })}
        </ul>
      </section>
    </>
  );
}

function Lobby({
  view,
  now,
  zone,
  seats,
}: {
  view: DraftView;
  now: number;
  zone: string;
  seats: DraftView['seats'];
}) {
  const startsIn = view.scheduledAt === null ? null : Date.parse(view.scheduledAt) - now;
  return (
    <>
      <section className="fsp-tv-block">
        <h2 className="fsp-tv-cap">The draft starts</h2>
        <div className="fsp-tv-pick">
          {view.scheduledAt === null
            ? 'When the commissioner starts it'
            : formatInZone(view.scheduledAt, zone, {
                weekday: 'long',
                hour: 'numeric',
                minute: '2-digit',
              })}
        </div>
        {startsIn !== null && startsIn > 0 ? (
          <div className="fsp-tv-clock fsp-num">{clock(startsIn)}</div>
        ) : null}
      </section>
      <section className="fsp-tv-block">
        <h2 className="fsp-tv-cap">The order</h2>
        <ol className="fsp-tv-next">
          {seats.map((s) => (
            <li key={s.team.id}>
              <span className="fsp-tv-nextno fsp-num">{s.slot}</span>
              <TeamLine seat={s} />
            </li>
          ))}
        </ol>
      </section>
    </>
  );
}

function Over({ view, seatOf }: { view: DraftView; seatOf: SeatMap }) {
  const last = view.lastPick;
  const lastSeat = last ? seatOf.get(last.teamId) : undefined;
  return (
    <section className="fsp-tv-block">
      <h2 className="fsp-tv-cap">The draft is over</h2>
      <div className="fsp-tv-pick">That’s the draft.</div>
      {last?.player && lastSeat ? (
        <div className="fsp-tv-note">
          {`The last pick: ${last.player.name} to ${lastSeat.team.name}.`}
        </div>
      ) : null}
    </section>
  );
}

/** Every team a column in its order, every round a row, the pick on the clock in view. */
function Board({
  view,
  seats,
  auction,
}: {
  view: DraftView;
  seats: DraftView['seats'];
  auction: boolean;
}) {
  const cells = useMemo(() => {
    const out = new Map<string, DraftView['board'][number]>();
    // EACH CELL IN ITS SLOT'S COLUMN, whoever a trade gave the pick to.
    for (const c of view.board) out.set(`${String(c.round)}:${c.originalTeamId ?? c.teamId}`, c);
    return out;
  }, [view.board]);
  const rounds = Array.from({ length: view.rounds }, (_, i) => i + 1);
  const at = view.current?.overall ?? null;
  const box = useRef<HTMLDivElement>(null);
  // THE PICK ON THE CLOCK IN VIEW, inside the board's own box only, so a
  // phone's clock side above it stays where it is.
  useEffect(() => {
    const scroller = box.current;
    const el = scroller?.querySelector<HTMLElement>('[data-current="true"]');
    if (!el || !scroller) return;
    const cell = el.getBoundingClientRect();
    const frame = scroller.getBoundingClientRect();
    scroller.scrollBy({
      left: cell.left - frame.left - (frame.width - cell.width) / 2,
      top: cell.top - frame.top - (frame.height - cell.height) / 2,
      behavior: 'smooth',
    });
  }, [at]);
  return (
    // A REGION THE KEYBOARD CAN SCROLL (UX-32): the board is wider and
    // longer than the window.
    <div className="fsp-tv-boardbox" ref={box} role="region" aria-label="The board" tabIndex={0}>
      <Colored className="fsp-tv-board" colors={{ '--fsp-seats': String(seats.length) }}>
        <div className="fsp-tv-corner-cell" aria-hidden>
          {auction ? '#' : 'Rd'}
        </div>
        {seats.map((s) => (
          <div key={s.team.id} className="fsp-tv-colhead">
            <TeamMark team={s.team} size={22} />
            <span className="fsp-tv-colname">{s.team.name}</span>
          </div>
        ))}
        {rounds.map((round) => (
          <BoardRow key={round} round={round} seats={seats} cells={cells} />
        ))}
      </Colored>
    </div>
  );
}

function BoardRow({
  round,
  seats,
  cells,
}: {
  round: number;
  seats: DraftView['seats'];
  cells: Map<string, DraftView['board'][number]>;
}) {
  return (
    <>
      <div className="fsp-tv-round fsp-num">{round}</div>
      {seats.map((s) => {
        const c = cells.get(`${String(round)}:${s.team.id}`);
        if (!c) return <div key={s.team.id} className="fsp-tv-cell" />;
        const pos = c.player?.positions[0]?.toLowerCase();
        return (
          <Colored
            key={s.team.id}
            className="fsp-tv-cell"
            data-filled={c.player ? 'true' : undefined}
            data-current={c.isCurrent ? 'true' : undefined}
            colors={pos ? { '--fsp-pos': `var(--fsp-pos-${pos})` } : {}}
          >
            {c.player ? (
              <>
                <span className="fsp-tv-cellname">{c.player.shortName}</span>
                <span className="fsp-tv-cellsub">
                  {positionLabel(c.player.positions[0] ?? '')} ·{' '}
                  {c.player.proTeam === null ? 'FA' : clubLabel(c.player.proTeam)}
                  {c.price !== null ? ` · $${String(c.price)}` : ''}
                </span>
              </>
            ) : c.isCurrent ? (
              <span className="fsp-tv-cellsub">On the clock</span>
            ) : null}
          </Colored>
        );
      })}
    </>
  );
}
